import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { settingsApi } from '../../lib/api';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

export function PrerequisitesDialog({ open, onOpenChange, onConfirm, seminarTitle }) {
    const [options, setOptions] = useState([]);
    const [notice, setNotice] = useState('');
    const [checked, setChecked] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (open) {
            loadPrerequisites();
            setChecked({});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const loadPrerequisites = async () => {
        setLoading(true);
        try {
            const res = await settingsApi.getPrerequisitesPublic();
            setOptions(res.data.options || []);
            setNotice(res.data.notice || '');
        } catch {
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    const hasSelection = Object.values(checked).some(v => v);

    const handleConfirm = () => {
        const selected = options.filter((_, i) => checked[i]);
        onConfirm(selected);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        Vorkenntnisse bestätigen
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <p className="text-sm text-muted-foreground">
                        Für die Anmeldung zur <strong>verkürzten Ausbildung</strong> {seminarTitle ? `"${seminarTitle}"` : ''} ist mindestens eine der folgenden Vorkenntnisse erforderlich:
                    </p>

                    {loading ? (
                        <div className="py-4 text-center text-muted-foreground text-sm">Laden...</div>
                    ) : options.length === 0 ? (
                        <div className="py-4 text-center text-muted-foreground text-sm">Keine Vorkenntnisse konfiguriert</div>
                    ) : (
                        <div className="space-y-2">
                            {options.map((opt, idx) => (
                                <label
                                    key={idx}
                                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                        checked[idx] ? 'bg-primary/5 border-primary/30' : 'bg-muted/20 border-gray-200 hover:bg-muted/40'
                                    }`}
                                    data-testid={`prereq-check-${idx}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked[idx] || false}
                                        onChange={(e) => setChecked(prev => ({ ...prev, [idx]: e.target.checked }))}
                                        className="mt-0.5 w-4 h-4 accent-primary"
                                    />
                                    <span className="text-sm flex-1">{opt}</span>
                                    {checked[idx] && <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />}
                                </label>
                            ))}
                        </div>
                    )}

                    {notice && (
                        <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                            {notice}
                        </p>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={!hasSelection}
                        data-testid="prereq-confirm-button"
                    >
                        Bestätigen und anmelden
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
